import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { IconArrowRight } from '@tabler/icons-react';
import styles from '../ReportMetrics.module.scss';
import detailStyles from '../widgets/Detail.module.scss';
import { KpiScope } from '../../../api/model';
import { KPI, numeric, numericByScopeId, phaseComparisonBars } from '../intelligence';
import { openActionsInArea, checkpointRows, inverseRateTone, scoreBand } from '../detail';
import { criticalityColors } from '../reportTheme';
import type { ReportViewProps } from './viewProps';
import { formatDay, pct, scoreStatus, tileDelta } from './tiles';
import ChartCard from '../widgets/ChartCard';
import MetricTile from '../widgets/MetricTile';
import ComparisonBarChart from '../widgets/ComparisonBarChart';
import DataTable from '../widgets/DataTable';
import StatusBadge from '../widgets/StatusBadge';
import InlineBar from '../widgets/InlineBar';
import ActionsHint from '../widgets/ActionsHint';

/**
 * Quality (Detailmasken §4): is the planned QA covered, is it keeping pace with
 * the operative progress, and where are checkpoints open or late. It measures
 * the documented QA activities, not the quality of the result - and never
 * mixes in the operative execution numbers.
 */
const Quality: React.FC<ReportViewProps> = ({
  kpis,
  actionPoints,
  historyIndex,
  findings,
  index,
  phaseNames,
  openTab,
  openSection,
  openObject,
}) => {
  const { t } = useTranslation(['templates', 'gantt']);

  const checkpoints = useMemo(
    () => checkpointRows(findings?.openCheckpoints, findings?.deliverables, phaseNames),
    [findings, phaseNames],
  );
  const delayed = checkpoints.filter((cp) => cp.delayed).length;

  const coverage = numeric(index, KPI.qaCoverage);
  const completion = numeric(index, KPI.projectQaCompletion);
  const timeliness = numeric(index, KPI.qaTimeliness);
  const assured = numeric(index, KPI.qualityAssuredProjectProgress);

  // Operative vs. assured is always the per-phase comparison, never a curve over time.
  const assuredByPhase = useMemo(
    () => phaseComparisonBars(kpis, KPI.phaseProgress, KPI.qualityAssuredPhaseProgress, phaseNames),
    [kpis, phaseNames],
  );

  const byPhase = useMemo(() => {
    const completionByPhase = numericByScopeId(kpis, KPI.phaseQaCompletion, KpiScope.PHASE);
    const timelinessByPhase = numericByScopeId(kpis, KPI.phaseQaTimeliness, KpiScope.PHASE);
    const delayedByPhase = numericByScopeId(kpis, KPI.phaseDelayedCheckpointRate, KpiScope.PHASE);
    return [...phaseNames.entries()].map(([id, name]) => ({
      id,
      name,
      completion: completionByPhase.get(id),
      timeliness: timelinessByPhase.get(id),
      delayed: delayedByPhase.get(id),
    }));
  }, [kpis, phaseNames]);

  const qualityActions = openActionsInArea(actionPoints, 'quality').length;

  return (
    <div className={styles.dashboard}>
      <div className={styles.tileRow}>
        <MetricTile
          label={t('report.kpi.qaCoverage.label')}
          value={pct(coverage)}
          delta={tileDelta(t, historyIndex, KPI.qaCoverage, coverage)}
          status={scoreStatus(t, coverage)}
          title={t('report.detail.quality.coverageHint')}
        />
        <MetricTile
          label={t('report.kpi.qaCompletion.label')}
          value={pct(completion)}
          delta={tileDelta(t, historyIndex, KPI.projectQaCompletion, completion)}
          status={scoreStatus(t, completion)}
        />
        <MetricTile
          label={t('report.kpi.qaTimeliness.label')}
          value={pct(timeliness)}
          delta={tileDelta(t, historyIndex, KPI.qaTimeliness, timeliness)}
          status={scoreStatus(t, timeliness)}
        />
        <MetricTile
          label={t('report.detail.quality.assured')}
          value={pct(assured)}
          delta={tileDelta(t, historyIndex, KPI.qualityAssuredProjectProgress, assured)}
          sub={t('report.detail.quality.ofProgress', { value: pct(numeric(index, KPI.projectProgress)) })}
          title={t('report.detail.quality.assuredHint')}
        />
        <MetricTile
          label={t('report.detail.quality.open')}
          value={`${checkpoints.length}`}
          footer={
            <button type="button" className={detailStyles.linkButton} onClick={() => openSection?.('check-plan')}>
              {t('report.detail.toList')}
              <IconArrowRight size={14} stroke={2} />
            </button>
          }
        />
        <MetricTile
          label={t('report.detail.quality.overdue')}
          value={`${delayed}`}
          status={delayed > 0 ? { tone: 'critical', label: t('report.detail.attention') } : undefined}
        />
      </div>

      <div className={detailStyles.halves}>
        <div>
          <ChartCard
            title={t('report.detail.quality.assuredTitle')}
            subtitle={t('report.detail.quality.assuredByPhase')}
          >
            {assuredByPhase.length > 0 ? (
              <ComparisonBarChart
                data={assuredByPhase}
                unit="%"
                max={100}
                plannedLabel={t('report.detail.quality.operative')}
                actualLabel={t('report.detail.quality.assured')}
              />
            ) : (
              <p className={styles.planDescription}>{t('report.state.notComputable')}</p>
            )}
          </ChartCard>
        </div>

        <ChartCard title={t('report.detail.quality.byPhase')} subtitle={t('report.detail.quality.byPhaseHint')} fitContent>
          <DataTable
            rows={byPhase}
            rowKey={(row) => row.id}
            empty={t('report.state.notComputable')}
            columns={[
              { key: 'phase', header: t('report.detail.col.phase'), render: (row) => row.name },
              {
                key: 'completion',
                header: t('report.detail.quality.completionShort'),
                render: (row) => <InlineBar value={row.completion} tone={scoreBand(row.completion)?.tone} />,
              },
              {
                key: 'timeliness',
                header: t('report.detail.quality.onTime'),
                align: 'right',
                render: (row) => pct(row.timeliness),
              },
              {
                key: 'delayed',
                header: t('report.detail.quality.delayedShare'),
                align: 'right',
                render: (row) => (
                  <span
                    className={
                      inverseRateTone(row.delayed) === 'critical' || inverseRateTone(row.delayed) === 'serious'
                        ? detailStyles.critical
                        : undefined
                    }
                  >
                    {pct(row.delayed)}
                  </span>
                ),
              },
            ]}
          />
        </ChartCard>
      </div>

      <ChartCard
        title={t('report.detail.quality.conspicuous')}
        subtitle={t('report.detail.quality.conspicuousHint')}
        action={
          <button type="button" className={detailStyles.linkButton} onClick={() => openSection?.('check-plan')}>
            {t('report.detail.allCheckpoints')}
            <IconArrowRight size={14} stroke={2} />
          </button>
        }
        fitContent
      >
        <DataTable
          rows={checkpoints}
          rowKey={(row) => row.id as number}
          limit={5}
          empty={t('report.detail.quality.noOpen')}
          onRowClick={(row) => openObject?.({ kind: 'checkpoint', id: row.id as number })}
          columns={[
            { key: 'deliverable', header: t('report.detail.col.deliverable'), render: (row) => row.deliverableName },
            { key: 'phase', header: t('report.detail.col.phase'), render: (row) => row.phaseName },
            {
              key: 'checkpoint',
              header: t('report.detail.col.checkpoint'),
              render: (row) => (
                <span title={row.label ?? undefined}>
                  {(row.label ?? row.taskName ?? `#${row.id}`).slice(0, 70)}
                  {(row.label?.length ?? 0) > 70 ? '…' : ''}
                </span>
              ),
            },
            {
              key: 'due',
              header: t('report.detail.col.due'),
              render: (row) => (
                <span className={row.delayed ? detailStyles.critical : undefined}>{formatDay(row.validationDate)}</span>
              ),
            },
            {
              key: 'status',
              header: t('report.detail.col.status'),
              render: (row) =>
                row.delayed ? (
                  <StatusBadge
                    tone="critical"
                    label={
                      row.daysOverdue != null
                        ? t('report.detail.daysLate', { count: row.daysOverdue })
                        : t('report.detail.overdue')
                    }
                  />
                ) : (
                  <StatusBadge tone="neutral" label={t('report.detail.quality.planned')} />
                ),
            },
            {
              key: 'criticality',
              header: t('report.detail.col.criticality'),
              render: (row) =>
                row.criticality ? (
                  <span style={{ color: criticalityColors[row.criticality], fontWeight: 600 }}>
                    {t(`report.criticality.${row.criticality}`)}
                  </span>
                ) : (
                  '—'
                ),
            },
          ]}
        />
      </ChartCard>

      <ActionsHint
        text={t('report.detail.actionsHint.quality', { count: qualityActions })}
        onOpen={() => openTab?.('actions')}
      />
    </div>
  );
};

export default Quality;
